import { useEffect, useMemo, useState } from 'react'
import { Check, Search, Users } from 'lucide-react'
import Modal from '../../../components/ERP/Modal/Modal'
import Avatar from '../../../components/Avatar/Avatar'
import { patientService } from '../../../services/patientApi'

function PatientAccessModal({ isOpen, patientId, viewers, onClose, onSaved }) {
  const [staff, setStaff] = useState([])
  const [selected, setSelected] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setSelected((viewers || []).map((user) => Number(user.id)))
    setSearch('')
    setError('')
    setLoading(true)
    patientService.staff()
      .then((response) => setStaff(response.data || []))
      .catch(() => setError('Não foi possível carregar os usuários.'))
      .finally(() => setLoading(false))
  }, [isOpen, viewers])

  const visibleStaff = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return staff
    return staff.filter((user) =>
      String(user.name || '').toLowerCase().includes(term)
      || String(user.email || '').toLowerCase().includes(term)
    )
  }, [search, staff])

  const toggle = (userId) => {
    setSelected((current) => (
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    ))
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const response = await patientService.setViewers(patientId, selected)
      onSaved(response.lostAccess ? null : response.data)
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível salvar o acesso')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Quem pode ver"
      subtitle="Se ninguém for escolhido, todos com permissão de pacientes veem este prontuário. Com alguém selecionado, só essas pessoas e os administradores."
      icon={<Users size={22} />}
      size="medium"
      footer={(
        <button type="button" className="btn-primary" onClick={save} disabled={saving || loading}>
          {saving ? 'Salvando...' : 'Salvar acesso'}
        </button>
      )}
    >
      <div className="patients-access-modal">
        {error && <p className="patients-alert-strip">{error}</p>}
        <label className="patients-field patients-access-search">
          <Search size={16} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome"
          />
        </label>
        <div className="patients-access-list">
          {loading && <p className="patients-muted">Carregando usuários...</p>}
          {!loading && visibleStaff.length === 0 && (
            <p className="patients-muted">Nenhum usuário encontrado.</p>
          )}
          {visibleStaff.map((user) => {
            const active = selected.includes(Number(user.id))
            return (
              <button
                key={user.id}
                type="button"
                className={`patients-access-user${active ? ' is-selected' : ''}`}
                onClick={() => toggle(Number(user.id))}
              >
                <Avatar name={user.name} email={user.email} imageUrl={user.avatar_url} size="md" />
                <span>
                  <strong>{user.name}</strong>
                  <small>{user.email}</small>
                </span>
                {active && <Check size={16} />}
              </button>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

export default PatientAccessModal
